import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Save, Eye, EyeOff, RotateCcw, Send, Loader2, Sparkles,
  AlertCircle, CheckCircle2, Rocket, RefreshCw, Image as ImageIcon,
  X, Plus, ArrowUp, ArrowDown, Monitor, Tablet, Smartphone, ChevronUp, ChevronDown,
  Palette, AlignLeft, AlignCenter, AlignRight, Maximize2,
  PlusSquare, Edit3, Copy, Trash2, Layers, GripVertical, History,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { websiteAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import useDraftPublish from '../../hooks/useDraftPublish';
import MediaPicker from '../../components/media/MediaPicker';
import BlockPickerModal from './components/BlockPickerModal';
import BlockEditPanel from './components/BlockEditPanel';
import HistoryPanel from './components/HistoryPanel';

/**
 * WebsiteBuilder — white-label template editor.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Top bar: page tabs + save/preview/publish                    │
 *   ├───────────────────────────┬─────────────────────────────────┤
 *   │  LIVE PREVIEW (iframe)    │  EDIT FORM (manifest-driven)    │
 *   │  70% width, full-height   │  30% width, scrollable          │
 *   │  device size toggle       │  grouped by manifest sections   │
 *   └───────────────────────────┴─────────────────────────────────┘
 *
 * The admin never sees sections/blocks — they only change text/image/colour
 * values. The Next.js template renders the same beautiful Trust Labs design
 * with the tenant's overrides merged in at runtime.
 */

const PAGE_TABS = [
  { key: 'layout',   label: 'عام (هيدر/فوتر)' },
  { key: 'home',     label: 'الرئيسية' },
  { key: 'about',    label: 'من نحن' },
  { key: 'contact',  label: 'تواصل معنا' },
  { key: 'services', label: 'الخدمات' },
  { key: 'packages', label: 'الباقات' },
  { key: 'offers',   label: 'العروض' },
  { key: 'blog',     label: 'المدونة' },
  { key: 'branches', label: 'الفروع' },
  { key: 'book',     label: 'الحجز' },
  { key: 'privacy',  label: 'الخصوصية' },
  { key: 'terms',    label: 'الشروط' },
];

const DEVICES = [
  { key: 'desktop', label: 'سطح المكتب', width: '100%',   icon: Monitor },
  { key: 'tablet',  label: 'تابلت',      width: '768px',  icon: Tablet },
  { key: 'mobile',  label: 'جوال',       width: '390px',  icon: Smartphone },
];

// PAGE_KEY_TO_PATH: maps manifest page key → URL path on the public site.
// 'layout' is a virtual key — it has no page of its own; we preview home.
const PAGE_KEY_TO_PATH = {
  layout: '/',
  home: '/', about: '/about', contact: '/contact',
  services: '/services', packages: '/packages', offers: '/offers',
  blog: '/blog', branches: '/branches', book: '/book',
  privacy: '/privacy', terms: '/terms',
};

function jsonEqual(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } }

// Flatten `manifest.pages` → `{ key: fieldSpec }` so we can look up a field's
// metadata (type, localised, default) in O(1) when an inline edit arrives.
function flattenManifest(pages) {
  const out = {};
  for (const page of Object.values(pages || {})) {
    for (const section of page.sections || []) {
      for (const field of section.fields || []) {
        out[field.key] = field;
      }
    }
  }
  return out;
}
function formatRelativeAr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'قبل لحظات';
  if (s < 3600) return `قبل ${Math.floor(s / 60)} دقيقة`;
  if (s < 86400) return `قبل ${Math.floor(s / 3600)} ساعة`;
  return d.toLocaleDateString('ar-SA-u-ca-gregory');
}

// =====================================================================
// Field input components
// =====================================================================

function LocaleTabs({ active, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 text-[11px]">
      {['ar', 'en'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          className={[
            'px-2 py-0.5 rounded font-medium transition-colors',
            active === l ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          ].join(' ')}
        >
          {l === 'ar' ? 'العربية' : 'English'}
        </button>
      ))}
    </div>
  );
}

function TextInput({ field, value, onChange }) {
  const [locale, setLocale] = useState('ar');
  if (!field.localised) {
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    );
  }
  const normalised = (value && typeof value === 'object') ? value : { ar: '', en: '' };
  return (
    <div>
      <div className="flex justify-end mb-1"><LocaleTabs active={locale} onChange={setLocale} /></div>
      <input
        type="text"
        value={normalised[locale] || ''}
        onChange={(e) => onChange({ ...normalised, [locale]: e.target.value })}
        dir={locale === 'en' ? 'ltr' : 'rtl'}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    </div>
  );
}

function TextAreaInput({ field, value, onChange }) {
  const [locale, setLocale] = useState('ar');
  if (!field.localised) {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    );
  }
  const normalised = (value && typeof value === 'object') ? value : { ar: '', en: '' };
  return (
    <div>
      <div className="flex justify-end mb-1"><LocaleTabs active={locale} onChange={setLocale} /></div>
      <textarea
        value={normalised[locale] || ''}
        onChange={(e) => onChange({ ...normalised, [locale]: e.target.value })}
        dir={locale === 'en' ? 'ltr' : 'rtl'}
        rows={field.type === 'richtext' ? 7 : 3}
        className={[
          'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
          field.type === 'richtext' ? 'font-mono' : '',
        ].join(' ')}
      />
      {field.type === 'richtext' && (
        <p className="text-[10px] text-gray-400 mt-0.5">HTML مسموح — استخدم &lt;p&gt;, &lt;strong&gt;, &lt;br&gt; إلخ</p>
      )}
    </div>
  );
}

function ImageInput({ field, value, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const url = (value && typeof value === 'object') ? (value.publicUrl || value.url || '') : value;
  return (
    <>
      {url ? (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-gray-50">
          <img src={url} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-gray-500 font-mono truncate" dir="ltr">{url}</div>
            <div className="flex items-center gap-1 mt-1">
              <button type="button" onClick={() => setPickerOpen(true)} className="text-[11px] text-blue-600 hover:underline">تغيير</button>
              <span className="text-gray-300">·</span>
              <button type="button" onClick={() => onChange('')} className="text-[11px] text-blue-600 hover:underline">إزالة</button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
        >
          <ImageIcon className="w-4 h-4" />
          اختر صورة من المكتبة
        </button>
      )}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(asset) => { onChange(asset?.publicUrl || ''); setPickerOpen(false); }}
        folder={field.folder || 'sections'}
      />
    </>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} dir="ltr" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" />
    </div>
  );
}

function ToggleInput({ field, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
      <span className="text-sm text-gray-700">{field.label}</span>
    </label>
  );
}

function SelectInput({ field, value, onChange }) {
  return (
    <select value={value ?? field.default ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white">
      {(field.options || []).map((o) => (
        <option key={o.value} value={o.value}>{o.label || o.labelAr || o.value}</option>
      ))}
    </select>
  );
}

function ListInput({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : (field.default || []);
  const setItem = (i, next) => onChange(items.map((it, idx) => (idx === i ? next : it)));
  const addItem = () => {
    const template = (field.itemFields || []).reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? (f.localised ? { ar: '', en: '' } : '') }), {});
    onChange([...(items || []), template]);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const moveItem = (i, delta) => {
    const to = i + delta;
    if (to < 0 || to >= items.length) return;
    const next = items.slice();
    const [m] = next.splice(i, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {(items || []).map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-500">#{i + 1}</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => removeItem(i)} className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-white"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {(field.itemFields || []).map((sub) => (
            <div key={sub.key}>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">{sub.label}</label>
              <FieldInput field={sub} value={item?.[sub.key]} onChange={(v) => setItem(i, { ...item, [sub.key]: v })} />
            </div>
          ))}
        </div>
      ))}
      <button type="button" onClick={addItem} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-xs text-gray-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
        <Plus className="w-3.5 h-3.5" />
        إضافة عنصر
      </button>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = { field, value, onChange };
  switch (field.type) {
    case 'text':      return <TextInput {...common} />;
    case 'textarea':  return <TextAreaInput {...common} />;
    case 'richtext':  return <TextAreaInput {...common} />;
    case 'image':     return <ImageInput {...common} />;
    case 'color':     return <ColorInput value={value ?? field.default} onChange={onChange} />;
    case 'toggle':    return <ToggleInput {...common} />;
    case 'select':    return <SelectInput {...common} />;
    case 'list':      return <ListInput {...common} />;
    default:          return <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />;
  }
}

// =====================================================================
// Main page
// =====================================================================

const WebsiteBuilder = () => {
  const { tenant } = useTenantBranding();
  const [pageKey, setPageKey] = useState('home');
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(true);
  const [pagesIndex, setPagesIndex] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [needsInit, setNeedsInit] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Local editable overrides for the current page (keyed by content key).
  const [localOverrides, setLocalOverrides] = useState({});
  const originalOverridesRef = useRef({});
  // Section visibility + order + typography. Flat map `{ [sectionId]:
  // { enabled, order, typography } }`. Empty by default → section renders
  // as manifest-defined.
  const [localSectionState, setLocalSectionState] = useState({});
  const originalSectionStateRef = useRef({});
  // Undo/redo stack — each entry is a `{ overrides, sectionState }` snapshot.
  // We push a snapshot whenever the admin makes a change that settles (after
  // 400ms of inactivity) so quick typing doesn't flood the stack.
  const undoStackRef = useRef([]);   // past states (oldest → newest)
  const redoStackRef = useRef([]);   // future states after undo
  const [historyVersion, setHistoryVersion] = useState(0); // forces re-render of undo/redo buttons
  const pushHistoryTimeoutRef = useRef(null);
  const [device, setDevice] = useState('desktop');
  const iframeRef = useRef(null);
  const [iframeNonce, setIframeNonce] = useState(0);
  const [previewTok, setPreviewTok] = useState(null);
  const [highlightKey, setHighlightKey] = useState(null);
  const fieldRefs = useRef({}); // key → DOM node
  const formScrollRef = useRef(null);
  // Which section currently has its Style panel expanded. Only one open at a
  // time so the form panel stays scannable.
  const [styleOpenFor, setStyleOpenFor] = useState(null);
  // Custom blocks state (Phase 2/3) — array of admin-added blocks for the
  // current page, kept in sync with `state.draftSnapshot.customBlocks`.
  const [localCustomBlocks, setLocalCustomBlocks] = useState([]);
  const originalCustomBlocksRef = useRef([]);
  // Per-field style state (Phase 6) — keyed by content key. Lets the admin
  // tweak one field's font-size/weight/color without affecting siblings.
  const [localFieldStyle, setLocalFieldStyle] = useState({});
  const originalFieldStyleRef = useRef({});
  // Which field's style panel is currently open (only one at a time, keeps
  // the form scannable).
  const [styleOpenForField, setStyleOpenForField] = useState(null);
  // Cached block library catalog (loaded lazily by the picker).
  const [blockCatalog, setBlockCatalog] = useState(null);
  // Picker modal + active edit panel state.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  // Drag-and-drop state — separate trackers for core sections vs custom
  // blocks so a drag in one area doesn't affect the other. We store the id
  // currently being dragged + the id being hovered for visual feedback.
  const [dragSectionId, setDragSectionId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const [dragBlockId, setDragBlockId] = useState(null);
  const [dragOverBlockId, setDragOverBlockId] = useState(null);
  // Phase 5 — History modal
  const [historyOpen, setHistoryOpen] = useState(false);

  // Bind API to current pageKey — useDraftPublish re-fetches when deps change.
  const api = useMemo(() => ({
    getState: () => websiteAPI.getPage(pageKey),
    saveDraft: (partial) => websiteAPI.savePageDraft(pageKey, partial),
    publish: () => websiteAPI.publishPage(pageKey),
    revert: () => websiteAPI.revertPage(pageKey),
    previewToken: () => websiteAPI.pagePreviewToken(pageKey),
  }), [pageKey]);

  const {
    state, loading, saving, publishing, reverting, previewLoading,
    saveDraft, publish, revert, openPreview, refetch,
  } = useDraftPublish({
    getState: api.getState,
    saveDraft: api.saveDraft,
    publish: api.publish,
    revert: api.revert,
    previewToken: api.previewToken,
  });

  // Manifest — reload whenever the tenant swaps templates so the editor's
  // default copy matches exactly what the live template renders. Without
  // this, a tenant on the Minimal template would see the Classic template's
  // defaults in the editor even though their site shows Minimal's copy.
  const templateSlug = tenant?.publicSite?.templateSlug || tenant?.templateSlug || '';
  useEffect(() => {
    let cancelled = false;
    setManifestLoading(true);
    (async () => {
      try {
        const resp = await websiteAPI.contentManifest(templateSlug);
        if (cancelled) return;
        setManifest(resp?.data?.data || resp?.data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.message || 'تعذّر تحميل المحتوى');
      } finally {
        if (!cancelled) setManifestLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [templateSlug]);

  // Pages-index fetch (also drives the "needs initialize" CTA).
  const fetchPagesIndex = useCallback(async () => {
    setPagesLoading(true);
    try {
      const resp = await websiteAPI.listPages();
      const body = resp?.data?.data || resp?.data;
      const pages = body?.pages || [];
      setPagesIndex(pages);
      setNeedsInit(!pages.some((p) => p.exists));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر تحميل قائمة الصفحات');
    } finally {
      setPagesLoading(false);
    }
  }, []);
  useEffect(() => { fetchPagesIndex(); }, [fetchPagesIndex]);

  // Seed local overrides from the server's draftSnapshot whenever state refreshes.
  useEffect(() => {
    const raw = state?.draftSnapshot?.overrides || {};
    const cloned = JSON.parse(JSON.stringify(raw));
    setLocalOverrides(cloned);
    originalOverridesRef.current = cloned;
  }, [state?.draftSnapshot?.overrides, pageKey]);

  // Seed section state from the server too — visibility + order.
  useEffect(() => {
    const raw = state?.draftSnapshot?.sectionState || {};
    const cloned = JSON.parse(JSON.stringify(raw));
    setLocalSectionState(cloned);
    originalSectionStateRef.current = cloned;
  }, [state?.draftSnapshot?.sectionState, pageKey]);

  // Seed custom blocks from server. Reset whenever the server state or
  // pageKey changes — blocks are page-scoped, switching tabs picks up the
  // right list.
  useEffect(() => {
    const raw = state?.draftSnapshot?.customBlocks || [];
    const cloned = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : [];
    setLocalCustomBlocks(cloned);
    originalCustomBlocksRef.current = cloned;
    // Also close any open edit panel — its block id might no longer exist
    // after switching to a different page.
    setEditingBlockId(null);
  }, [state?.draftSnapshot?.customBlocks, pageKey]);

  // Seed per-field style map from server.
  useEffect(() => {
    const raw = state?.draftSnapshot?.fieldStyle || {};
    const cloned = JSON.parse(JSON.stringify(raw));
    setLocalFieldStyle(cloned);
    originalFieldStyleRef.current = cloned;
    setStyleOpenForField(null);
  }, [state?.draftSnapshot?.fieldStyle, pageKey]);

  // Reset Undo/Redo history when the user switches pages. Without this, the
  // history would carry over edits from the previous page and applying Undo
  // would restore the wrong page's content into the current tab — silent
  // data corruption. The history must be page-scoped.
  useEffect(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setHistoryVersion((v) => v + 1);
  }, [pageKey]);

  // Phase 5 — beforeunload guard. If the admin has unsaved changes, the
  // browser shows a "Leave page?" dialog. Without this, switching tabs or
  // closing the browser silently loses the in-flight edits.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onBeforeUnload = (e) => {
      // We only check `dirty` (local edits not sent yet). serverDirty (saved
      // but not published) is fine to leave — it persists in DB.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = ''; // Chrome requires this to trigger the prompt
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Pre-load the block catalog on mount so addCustomBlock has data ready
  // when the user clicks a block in the picker. Without this, fast clicks
  // could land before the lazy fetch completed and addCustomBlock would
  // toast "نوع غير معروف" even for valid types.
  useEffect(() => {
    if (blockCatalog) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await websiteAPI.blockLibrary();
        if (cancelled) return;
        setBlockCatalog(resp?.data?.data || resp?.data);
      } catch (_) { /* picker shows its own error UI; addCustomBlock will toast */ }
    })();
    return () => { cancelled = true; };
  }, [blockCatalog]);

  // Debounced history snapshot: the admin is *still* typing? wait. Once they
  // stop for 400ms, take a snapshot of overrides + sectionState + customBlocks
  // so Undo has something meaningful to revert to.
  useEffect(() => {
    if (pushHistoryTimeoutRef.current) clearTimeout(pushHistoryTimeoutRef.current);
    pushHistoryTimeoutRef.current = setTimeout(() => {
      const top = undoStackRef.current[undoStackRef.current.length - 1];
      const snapshot = {
        overrides: JSON.parse(JSON.stringify(localOverrides)),
        sectionState: JSON.parse(JSON.stringify(localSectionState)),
        customBlocks: JSON.parse(JSON.stringify(localCustomBlocks)),
      };
      if (top && jsonEqual(top, snapshot)) return;
      undoStackRef.current.push(snapshot);
      if (undoStackRef.current.length > 50) undoStackRef.current.shift(); // bounded
      // Any new edit invalidates the redo stack.
      if (redoStackRef.current.length) redoStackRef.current = [];
      setHistoryVersion((v) => v + 1);
    }, 400);
    return () => clearTimeout(pushHistoryTimeoutRef.current);
  }, [localOverrides, localSectionState, localCustomBlocks]);

  const canUndo = undoStackRef.current.length > 1;
  const canRedo = redoStackRef.current.length > 0;
  const doUndo = useCallback(() => {
    if (undoStackRef.current.length <= 1) return;
    const current = undoStackRef.current.pop();
    redoStackRef.current.push(current);
    const prev = undoStackRef.current[undoStackRef.current.length - 1];
    setLocalOverrides(prev.overrides);
    setLocalSectionState(prev.sectionState);
    if (Array.isArray(prev.customBlocks)) setLocalCustomBlocks(prev.customBlocks);
    setHistoryVersion((v) => v + 1);
  }, []);
  const doRedo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push(next);
    setLocalOverrides(next.overrides);
    setLocalSectionState(next.sectionState);
    if (Array.isArray(next.customBlocks)) setLocalCustomBlocks(next.customBlocks);
    setHistoryVersion((v) => v + 1);
  }, []);

  // Cmd/Ctrl+Z → undo, Cmd/Ctrl+Shift+Z (or Cmd+Y) → redo.
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when the admin is typing in an input.
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); doUndo(); }
      else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); doRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doUndo, doRedo]);

  const dirty = useMemo(
    () => !jsonEqual(localOverrides, originalOverridesRef.current)
      || !jsonEqual(localSectionState, originalSectionStateRef.current)
      || !jsonEqual(localCustomBlocks, originalCustomBlocksRef.current)
      || !jsonEqual(localFieldStyle, originalFieldStyleRef.current),
    [localOverrides, localSectionState, localCustomBlocks, localFieldStyle]
  );
  // Mirror dirty into a ref so the beforeunload handler (a stable closure
  // attached once) reads the latest value without re-binding on every change.
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  // Mirror localCustomBlocks into a ref so the iframe message listener can
  // verify a block id exists before opening the edit panel — without
  // pulling the latest array into its useEffect deps (which would re-bind
  // the global postMessage listener on every block edit and risk dropped
  // messages mid-edit).
  const customBlocksRef = useRef([]);
  useEffect(() => { customBlocksRef.current = localCustomBlocks; }, [localCustomBlocks]);
  const serverDirty = !!state?.hasUnpublishedChanges;
  const anyDirty = dirty || serverDirty;

  const onInitialize = useCallback(async () => {
    setInitializing(true);
    try {
      await websiteAPI.initialize();
      toast.success('تم تفعيل الموقع');
      await fetchPagesIndex();
      await refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر التفعيل');
    } finally { setInitializing(false); }
  }, [fetchPagesIndex, refetch]);

  const onSaveDraft = useCallback(async () => {
    if (!dirty) return;
    const data = await saveDraft({ overrides: localOverrides, sectionState: localSectionState, customBlocks: localCustomBlocks, fieldStyle: localFieldStyle });
    if (data) {
      toast.success('تم حفظ المسودة');
      setIframeNonce((n) => n + 1); // nudge the iframe so preview refreshes
    }
  }, [dirty, saveDraft, localOverrides, localSectionState, localCustomBlocks]);

  const onPublish = useCallback(async () => {
    if (dirty) {
      const saved = await saveDraft({ overrides: localOverrides, sectionState: localSectionState, customBlocks: localCustomBlocks, fieldStyle: localFieldStyle });
      if (!saved) return;
    }
    await publish();
    setIframeNonce((n) => n + 1);
    fetchPagesIndex();
  }, [dirty, saveDraft, localOverrides, localSectionState, localCustomBlocks, publish, fetchPagesIndex]);

  const onRevert = useCallback(async () => {
    // Confirm before discarding in-flight edits. Revert is destructive —
    // current draft is replaced by the published snapshot, no undo path.
    const ok = window.confirm(
      'سيتم إلغاء جميع التعديلات الحالية واستعادة آخر نسخة منشورة.\n\n' +
      'هل أنت متأكد؟'
    );
    if (!ok) return;
    const data = await revert();
    if (data) {
      fetchPagesIndex();
      setIframeNonce((n) => n + 1);
    }
  }, [revert, fetchPagesIndex]);

  const onPreviewOpen = useCallback(async () => {
    if (dirty) {
      const saved = await saveDraft({ overrides: localOverrides, sectionState: localSectionState, customBlocks: localCustomBlocks, fieldStyle: localFieldStyle });
      if (!saved) return;
    }
    await openPreview();
  }, [dirty, saveDraft, localOverrides, localSectionState, localCustomBlocks, openPreview]);

  // ── Custom block helpers (Phase 2/3) ─────────────────────────────
  // All operations work against `localCustomBlocks` and rely on saveDraft
  // to persist. Block IDs follow `cb_<random>` so the backend can spot
  // freshly-created vs persisted blocks (it'll re-mint malformed ones).
  const newBlockId = () => 'cb_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

  const addCustomBlock = useCallback((blockType) => {
    const def = blockCatalog?.blocks?.[blockType];
    if (!def) {
      toast.error('نوع غير معروف: ' + blockType);
      return;
    }
    // Build a default `data` from each schema field's `default`.
    const data = {};
    for (const f of (def.schema?.fields || [])) {
      if (f.default !== undefined) data[f.key] = JSON.parse(JSON.stringify(f.default));
    }
    const block = {
      id: newBlockId(),
      type: blockType,
      enabled: true,
      order: localCustomBlocks.length,
      data,
      style: def.defaultStyle ? JSON.parse(JSON.stringify(def.defaultStyle)) : {},
    };
    setLocalCustomBlocks([...localCustomBlocks, block]);
    setPickerOpen(false);
    setEditingBlockId(block.id);
    toast.success('تمت إضافة القسم — حرّر المحتوى ثم احفظ');
  }, [blockCatalog, localCustomBlocks]);

  const updateCustomBlock = useCallback((id, patch) => {
    setLocalCustomBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, []);

  const deleteCustomBlock = useCallback((id) => {
    setLocalCustomBlocks((prev) => prev.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })));
    setEditingBlockId((cur) => (cur === id ? null : cur));
  }, []);

  const duplicateCustomBlock = useCallback((id) => {
    setLocalCustomBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx < 0) return prev;
      const copy = JSON.parse(JSON.stringify(prev[idx]));
      copy.id = newBlockId();
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next.map((b, i) => ({ ...b, order: i }));
    });
  }, []);

  const moveCustomBlock = useCallback((id, direction) => {
    setLocalCustomBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((b, i) => ({ ...b, order: i }));
    });
  }, []);

  const toggleCustomBlock = useCallback((id) => {
    setLocalCustomBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, enabled: b.enabled === false ? true : false } : b)));
  }, []);

  // The block currently being edited in the side panel.
  const editingBlock = editingBlockId ? localCustomBlocks.find((b) => b.id === editingBlockId) : null;
  const editingBlockDef = editingBlock ? blockCatalog?.blocks?.[editingBlock.type] : null;

  // Fetch (or refresh) a preview token every time pageKey changes — tokens
  // are scoped to the current page:resource so the iframe sees the draft
  // (not the published) overrides.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await websiteAPI.pagePreviewToken(pageKey);
        if (cancelled) return;
        const body = resp?.data?.data || resp?.data;
        setPreviewTok(body?.token || null);
      } catch (_) { /* preview falls back silently */ }
    })();
    return () => { cancelled = true; };
  }, [pageKey]);

  const previewUrl = useMemo(() => {
    const slug = tenant?.slug;
    const path = PAGE_KEY_TO_PATH[pageKey] || '/';
    const base = slug ? `https://${slug}.sites.listechs.com` : '';
    if (!base) return '';
    // `_editor=1` enables ClickToEditBridge + data-content-key emission.
    // `preview=<token>` swaps published → draft content with cache: no-store.
    // `_v=<nonce>` busts the browser cache on save/publish.
    const q = new URLSearchParams();
    q.set('_editor', '1');
    if (previewTok) q.set('preview', previewTok);
    q.set('_v', String(iframeNonce));
    return `${base}${path}?${q.toString()}`;
  }, [tenant?.slug, pageKey, iframeNonce, previewTok]);

  // Cross-iframe messages from the preview. Two protocols:
  //   - `edit-request {key}`    → scroll to + focus the matching form field
  //                               (fired on single click)
  //   - `content-update {key,value}` → commit an inline edit the admin made
  //                                    directly in the canvas (fired on blur
  //                                    after double-click contenteditable)
  useEffect(() => {
    const onMessage = (ev) => {
      const data = ev?.data;
      if (!data || data.source !== 'listechs-site') return;

      if (data.type === 'edit-request') {
        const key = data.key;
        if (!key) return;
        setHighlightKey(key);
        const node = fieldRefs.current[key];
        if (node && formScrollRef.current) {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const first = node.querySelector('input, textarea, select, button');
          if (first) first.focus?.();
        }
        setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 2500);
        return;
      }

      // Custom-block click in the iframe → open that block's edit panel
      // (Phase 4 inline editing). Verify the id via the ref so we don't
      // need to add localCustomBlocks to deps (which would re-bind the
      // postMessage listener on every block edit).
      if (data.type === 'edit-block-request') {
        const bid = data.blockId;
        if (!bid) return;
        const exists = customBlocksRef.current.some((b) => b.id === bid);
        if (exists) setEditingBlockId(bid);
        return;
      }

      if (data.type === 'content-update') {
        const key = data.key;
        const value = data.value;
        if (!key || value == null) return;
        // Inline edits arrive as a plain string. If the manifest says the key
        // is localised ({ ar, en }), merge into the current locale so the
        // other locale isn't clobbered.
        const flat = manifest?.pages ? flattenManifest(manifest.pages) : {};
        const field = flat[key];
        setLocalOverrides((prev) => {
          const existing = prev[key];
          if (field?.localised) {
            const base = (typeof existing === 'object' && existing !== null) ? existing : {};
            return { ...prev, [key]: { ...base, ar: value, en: base.en || value } };
          }
          return { ...prev, [key]: value };
        });
        setHighlightKey(key);
        setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 1500);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [manifest]);

  // Manifest page for the current pageKey.
  const manifestPage = manifest?.pages?.[pageKey] || null;

  // ─── Render branches ─────────────────────────────────────────
  if (manifestLoading && pagesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" dir="rtl">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (needsInit && !pagesLoading) {
    return (
      <div className="container mx-auto px-6 py-12" dir="rtl">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="p-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-extrabold">فعّل موقعك الإلكتروني</h1>
              <p className="text-blue-100 text-sm mt-2">موقعك جاهز — اضغط للتفعيل وابدأ التعديل.</p>
            </div>
            <div className="p-6">
              <ul className="space-y-2 text-sm text-gray-600 mb-5">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>11 صفحة جاهزة بتصميم احترافي مطابق لمختبرات ثقة.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>كل النصوص والصور والروابط قابلة للتخصيص.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>معاينة مباشرة أثناء التعديل.</span></li>
              </ul>
              <button onClick={onInitialize} disabled={initializing} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold disabled:opacity-50 shadow-lg shadow-blue-200">
                {initializing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
                {initializing ? 'جاري التفعيل...' : 'تفعيل موقعي الآن'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden" dir="rtl">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm shrink-0">
        <div className="px-4 py-2 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
              <LayoutDashboard className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-800">محرّر الموقع</div>
              <div className="text-[10px] text-gray-400">قالب أبيض — عدّل النصوص والصور بدون إعادة بناء</div>
            </div>
          </div>

          <div className="flex-1" />

          {/* Device switcher */}
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            {DEVICES.map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.key} type="button" onClick={() => setDevice(d.key)}
                  className={[
                    'px-2 py-1 rounded-md text-[11px] font-medium transition-colors inline-flex items-center gap-1',
                    device === d.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  title={d.label}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {d.label}
                </button>
              );
            })}
          </div>

          <button onClick={() => setIframeNonce((n) => n + 1)} className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-50" title="إعادة تحميل المعاينة">
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            onClick={onPreviewOpen}
            disabled={previewLoading || saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
          >
            {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            فتح في تاب جديد
          </button>
          {/* Undo/Redo — Cmd/Ctrl+Z / Cmd+Shift+Z. Uses debounced history
              stack so rapid typing doesn't create a hundred entries. */}
          <button
            onClick={doUndo}
            disabled={!canUndo}
            title="تراجع (⌘Z)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            data-history-version={historyVersion}
          >
            <ArrowDown className="w-3.5 h-3.5 rotate-90 scale-x-[-1]" />
            تراجع
          </button>
          <button
            onClick={doRedo}
            disabled={!canRedo}
            title="إعادة (⌘⇧Z)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
          >
            <ArrowDown className="w-3.5 h-3.5 rotate-90" />
            إعادة
          </button>
          <button
            onClick={onRevert}
            disabled={reverting || !serverDirty}
            title="استعادة آخر نسخة منشورة"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
          >
            {reverting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            استعادة المنشور
          </button>
          <button
            onClick={() => setHistoryOpen(true)}
            title="تاريخ النشر — استعادة نسخة سابقة"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
          >
            <History className="w-3.5 h-3.5" />
            السجل
          </button>
          <button
            onClick={onSaveDraft}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            حفظ مسودة
          </button>
          <button
            onClick={onPublish}
            disabled={!anyDirty || publishing}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-40 shadow-sm"
          >
            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            نشر
          </button>
        </div>

        {/* Page tabs */}
        <div className="border-t border-gray-100 flex overflow-x-auto scrollbar-thin">
          {PAGE_TABS.map((p) => {
            const pageInfo = pagesIndex.find((pi) => pi.pageKey === p.key);
            const active = p.key === pageKey;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPageKey(p.key)}
                className={[
                  'relative px-3 py-2 whitespace-nowrap text-xs font-medium transition-colors border-l border-gray-100 last:border-l-0 shrink-0',
                  active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50',
                ].join(' ')}
              >
                {p.label}
                {pageInfo?.hasUnpublishedChanges && (
                  <span className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" title="تعديلات غير منشورة" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main split view */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Preview (LEFT when RTL, shows on the visual right thanks to flex row + dir=rtl) */}
        <div className="flex-1 p-3 flex items-start justify-center bg-gradient-to-br from-gray-100 to-gray-50 overflow-auto">
          <div
            className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 transition-all duration-300"
            style={{ width: DEVICES.find((d) => d.key === device)?.width, maxWidth: '100%', height: 'calc(100vh - 150px)' }}
          >
            {previewUrl ? (
              <iframe
                ref={iframeRef}
                key={iframeNonce + previewUrl}
                src={previewUrl}
                title="معاينة الموقع"
                className="w-full h-full border-0"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-6 text-center">
                <Sparkles className="w-8 h-8 mb-2" />
                <p className="text-sm">المعاينة ستظهر هنا — بعد تفعيل الموقع على الـ subdomain</p>
                <p className="text-[11px] mt-2">slug: {tenant?.slug || '—'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Edit form (RIGHT — appears on the visual left in RTL) */}
        <div ref={formScrollRef} className="w-[420px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
          ) : !manifestPage ? (
            <div className="p-6 text-center text-sm text-gray-400">لا يوجد محتوى قابل للتعديل على هذه الصفحة</div>
          ) : (
            <div className="p-4">
              <div className="mb-4">
                <h2 className="text-sm font-bold text-gray-800">{manifestPage.label}</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {(state?.draftUpdatedAt) ? `آخر حفظ: ${formatRelativeAr(state.draftUpdatedAt)}` : 'لم تحفظ بعد'}
                  {dirty && <span className="mr-1 inline-flex items-center gap-1 text-amber-600"><AlertCircle className="w-3 h-3" />غير محفوظ</span>}
                </p>
              </div>
              <div className="space-y-5">
                {(() => {
                  // Order sections by saved sectionState.order (if any), otherwise
                  // keep manifest order. Lets the admin reorder via the controls.
                  const baseSections = manifestPage.sections || [];
                  const ordered = [...baseSections].sort((a, b) => {
                    const ao = localSectionState[a.id]?.order;
                    const bo = localSectionState[b.id]?.order;
                    const ai = typeof ao === 'number' ? ao : (typeof a.defaultOrder === 'number' ? a.defaultOrder : 0);
                    const bi = typeof bo === 'number' ? bo : (typeof b.defaultOrder === 'number' ? b.defaultOrder : 0);
                    return ai - bi;
                  });
                  return ordered.map((section, idx) => {
                    const state = localSectionState[section.id] || {};
                    const isHidden = state.enabled === false;
                    const canToggle = section.canToggle !== false;
                    const canReorder = section.canReorder !== false;
                    // Generic patch that preserves every other key on the
                    // section's state (typography won't drop background; bg
                    // edits won't drop padding; etc.).
                    const patchSectionState = (patch) => {
                      const current = localSectionState[section.id] || {};
                      setLocalSectionState({
                        ...localSectionState,
                        [section.id]: {
                          order: typeof current.order === 'number' ? current.order : (section.defaultOrder || idx),
                          enabled: current.enabled !== false,
                          ...current,
                          ...patch,
                        },
                      });
                    };
                    const moveSection = (direction) => {
                      const target = idx + direction;
                      if (target < 0 || target >= ordered.length) return;
                      // Don't allow swapping past a locked section (e.g. the
                      // mandatory hero). Without this guard, repeated up-arrows
                      // could push other sections above hero, breaking the
                      // intended page layout.
                      if (ordered[target]?.canReorder === false) return;
                      const newList = [...ordered];
                      [newList[idx], newList[target]] = [newList[target], newList[idx]];
                      const next = { ...localSectionState };
                      newList.forEach((s, i) => {
                        next[s.id] = { ...(next[s.id] || {}), order: i, enabled: next[s.id]?.enabled !== false };
                      });
                      setLocalSectionState(next);
                    };
                    const toggleVisibility = () => {
                      patchSectionState({ enabled: state.enabled === false });
                    };
                    const setTypography = (tp) => {
                      patchSectionState({ typography: { ...(state.typography || {}), ...tp } });
                    };
                    const setBackground = (bg) => {
                      // Setting type:'none' clears the background entirely.
                      if (!bg || bg.type === 'none') {
                        const { background: _drop, ...rest } = state;
                        setLocalSectionState({ ...localSectionState, [section.id]: { ...rest, enabled: state.enabled !== false, order: typeof state.order === 'number' ? state.order : (section.defaultOrder || idx) } });
                        return;
                      }
                      patchSectionState({ background: { ...(state.background || {}), ...bg } });
                    };
                    const setPadding = (pd) => {
                      patchSectionState({ padding: { ...(state.padding || {}), ...pd } });
                    };
                    const setContainerWidth = (cw) => patchSectionState({ containerWidth: cw || undefined });
                    const setTextAlign = (ta) => patchSectionState({ textAlign: ta || undefined });
                    const typo = state.typography || {};
                    const bg = state.background || {};
                    const pad = state.padding || {};
                    // Drag-and-drop handlers — only enabled when the section
                    // is reorderable. dataTransfer carries the id; we track
                    // hover for the drop indicator.
                    const dragHandlers = canReorder ? {
                      draggable: true,
                      onDragStart: (e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', section.id);
                        setDragSectionId(section.id);
                      },
                      onDragOver: (e) => {
                        if (!dragSectionId || dragSectionId === section.id) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverSectionId(section.id);
                      },
                      onDragLeave: () => {
                        if (dragOverSectionId === section.id) setDragOverSectionId(null);
                      },
                      onDrop: (e) => {
                        e.preventDefault();
                        const sourceId = e.dataTransfer.getData('text/plain') || dragSectionId;
                        if (!sourceId || sourceId === section.id) {
                          setDragSectionId(null);
                          setDragOverSectionId(null);
                          return;
                        }
                        const sourceIdx = ordered.findIndex((s) => s.id === sourceId);
                        const destIdx = ordered.findIndex((s) => s.id === section.id);
                        if (sourceIdx < 0 || destIdx < 0) {
                          setDragSectionId(null); setDragOverSectionId(null); return;
                        }
                        // Block dropping onto/across a locked section (hero).
                        // We check if the move would cross the locked section.
                        const movingDown = sourceIdx < destIdx;
                        const lo = Math.min(sourceIdx, destIdx);
                        const hi = Math.max(sourceIdx, destIdx);
                        const blockedRange = ordered.slice(lo, hi + 1).some((s) => s.canReorder === false && s.id !== sourceId);
                        if (blockedRange) {
                          setDragSectionId(null); setDragOverSectionId(null); return;
                        }
                        const newList = [...ordered];
                        const [moved] = newList.splice(sourceIdx, 1);
                        newList.splice(destIdx, 0, moved);
                        const next = { ...localSectionState };
                        newList.forEach((s, i) => {
                          next[s.id] = { ...(next[s.id] || {}), order: i, enabled: next[s.id]?.enabled !== false };
                        });
                        setLocalSectionState(next);
                        setDragSectionId(null);
                        setDragOverSectionId(null);
                      },
                      onDragEnd: () => {
                        setDragSectionId(null);
                        setDragOverSectionId(null);
                      },
                    } : {};
                    const isDragging = dragSectionId === section.id;
                    const isDragOver = dragOverSectionId === section.id;
                    return (
                  <div
                    key={section.id}
                    data-section-card={section.id}
                    {...dragHandlers}
                    className={[
                      'rounded-xl border overflow-hidden transition-all',
                      isHidden ? 'border-amber-300 bg-amber-50/30' : 'border-gray-100',
                      isDragging ? 'opacity-50' : '',
                      isDragOver ? 'border-blue-500 ring-2 ring-blue-300 shadow-md' : '',
                    ].join(' ')}>
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {canReorder && (
                          <span title="اسحب لإعادة الترتيب" className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0">
                            <GripVertical className="w-3.5 h-3.5" />
                          </span>
                        )}
                        <h3 className={`text-[11px] font-bold truncate ${isHidden ? 'text-amber-700 line-through' : 'text-gray-700'}`}>
                          {section.label}
                          {isHidden && <span className="mr-2 font-normal no-underline">(مخفي)</span>}
                        </h3>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {canReorder && (
                          <>
                            <button type="button" onClick={() => moveSection(-1)} disabled={idx === 0}
                              title="حرّك للأعلى"
                              className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed">
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => moveSection(+1)} disabled={idx === ordered.length - 1}
                              title="حرّك للأسفل"
                              className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed">
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {canToggle && (
                          <button type="button" onClick={toggleVisibility}
                            title={isHidden ? 'أظهر هذا القسم' : 'أخفِ هذا القسم'}
                            className={`p-1 transition-colors ${isHidden ? 'text-amber-600 hover:text-amber-700' : 'text-gray-500 hover:text-blue-600'}`}>
                            {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button type="button"
                          onClick={() => setStyleOpenFor((cur) => cur === section.id ? null : section.id)}
                          title="ستايل القسم (خلفية، حواف، محاذاة)"
                          className={`p-1 transition-colors ${styleOpenFor === section.id ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>
                          <Palette className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {styleOpenFor === section.id && !isHidden && (
                      <div className="px-3 py-3 bg-blue-50/30 border-b border-blue-100 space-y-3">
                        {/* Background */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-bold text-gray-700">الخلفية</span>
                            {bg.type && bg.type !== 'none' && (
                              <button type="button" onClick={() => setBackground({ type: 'none' })}
                                className="text-[10px] text-red-500 hover:text-red-600">إعادة تعيين</button>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            {['none', 'color', 'image'].map((bgt) => (
                              <button key={bgt} type="button"
                                onClick={() => setBackground({ type: bgt })}
                                className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${(bg.type || 'none') === bgt ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                {bgt === 'none' ? 'بدون' : bgt === 'color' ? 'لون' : 'صورة'}
                              </button>
                            ))}
                          </div>
                          {bg.type === 'color' && (
                            <div className="flex items-center gap-2">
                              <input type="color" value={bg.color || '#ffffff'}
                                onChange={(e) => setBackground({ type: 'color', color: e.target.value })}
                                className="w-9 h-9 rounded border border-gray-200 cursor-pointer p-0" />
                              <input type="text" value={bg.color || ''}
                                onChange={(e) => setBackground({ type: 'color', color: e.target.value })}
                                placeholder="#ffffff"
                                className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-[11px] font-mono"
                                dir="ltr" />
                            </div>
                          )}
                          {bg.type === 'image' && (
                            <input type="text" value={bg.imageUrl || ''}
                              onChange={(e) => setBackground({ type: 'image', imageUrl: e.target.value })}
                              placeholder="https://... أو /path/image.jpg"
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-[11px] font-mono"
                              dir="ltr" />
                          )}
                        </div>
                        {/* Padding */}
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">
                            <span>هامش علوي ({pad.top ?? 0}px{pad.top == null ? ' — افتراضي' : ''})</span>
                            <input type="range" min={0} max={200} step={4}
                              value={pad.top ?? 0}
                              onChange={(e) => setPadding({ top: Number(e.target.value) })}
                              className="h-1 accent-blue-600" />
                          </label>
                          <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">
                            <span>هامش سفلي ({pad.bottom ?? 0}px{pad.bottom == null ? ' — افتراضي' : ''})</span>
                            <input type="range" min={0} max={200} step={4}
                              value={pad.bottom ?? 0}
                              onChange={(e) => setPadding({ bottom: Number(e.target.value) })}
                              className="h-1 accent-blue-600" />
                          </label>
                        </div>
                        {/* Container width */}
                        <div>
                          <div className="text-[10px] font-bold text-gray-700 mb-1">عرض القسم</div>
                          <div className="grid grid-cols-4 gap-1">
                            {[
                              { v: '', label: 'افتراضي' },
                              { v: 'narrow', label: 'ضيق' },
                              { v: 'normal', label: 'عادي' },
                              { v: 'wide', label: 'واسع' },
                            ].map((o) => (
                              <button key={o.v || 'default'} type="button"
                                onClick={() => setContainerWidth(o.v)}
                                className={`px-1 py-1 rounded-md text-[10px] font-medium transition-colors ${(state.containerWidth || '') === o.v ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Text alignment */}
                        <div>
                          <div className="text-[10px] font-bold text-gray-700 mb-1">محاذاة النص</div>
                          <div className="grid grid-cols-4 gap-1">
                            {[
                              { v: '', icon: null, label: 'افتراضي' },
                              { v: 'start', icon: AlignRight, label: 'يمين' },
                              { v: 'center', icon: AlignCenter, label: 'وسط' },
                              { v: 'end', icon: AlignLeft, label: 'يسار' },
                            ].map((o) => {
                              const Icon = o.icon;
                              return (
                                <button key={o.v || 'default'} type="button"
                                  onClick={() => setTextAlign(o.v)}
                                  className={`px-1 py-1.5 rounded-md text-[10px] font-medium transition-colors inline-flex items-center justify-center gap-0.5 ${(state.textAlign || '') === o.v ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                  {Icon ? <Icon className="w-3 h-3" /> : <span>—</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {!isHidden && (
                      <div className="px-3 py-2 bg-slate-50/60 border-b border-gray-100 grid grid-cols-3 gap-2">
                        <label className="flex flex-col gap-0.5 text-[10px] text-gray-500" title="ينطبق على العناوين فقط (h1-h6)، وليس على الفقرات أو الأزرار">
                          <span>حجم العناوين ({typo.fontSize ?? '–'})</span>
                          <input type="range" min={12} max={80} step={1}
                            value={typo.fontSize ?? 16}
                            onChange={(e) => setTypography({ fontSize: Number(e.target.value) })}
                            className="h-1 accent-blue-600" />
                        </label>
                        <label className="flex flex-col gap-0.5 text-[10px] text-gray-500" title="ينطبق على العناوين فقط (h1-h6)">
                          <span>سُمك العناوين ({typo.fontWeight ?? '–'})</span>
                          <input type="range" min={300} max={900} step={100}
                            value={typo.fontWeight ?? 500}
                            onChange={(e) => setTypography({ fontWeight: Number(e.target.value) })}
                            className="h-1 accent-blue-600" />
                        </label>
                        <label className="flex flex-col gap-0.5 text-[10px] text-gray-500" title="ينطبق على الفقرات والقوائم">
                          <span>تباعد الفقرات ({(typo.lineHeight ?? 1.5).toFixed(1)})</span>
                          <input type="range" min={1} max={2.5} step={0.1}
                            value={typo.lineHeight ?? 1.5}
                            onChange={(e) => setTypography({ lineHeight: Number(e.target.value) })}
                            className="h-1 accent-blue-600" />
                        </label>
                      </div>
                    )}
                    <div className="p-3 space-y-3">
                      {(section.fields || []).map((field) => {
                        // Per-field style support — only meaningful for
                        // visible-text field types (others have nothing to
                        // restyle).
                        const STYLEABLE = field.type === 'text' || field.type === 'textarea' || field.type === 'richtext';
                        const fStyle = localFieldStyle[field.key] || {};
                        const fStyleOpen = styleOpenForField === field.key;
                        const setFStyle = (patch) => {
                          setLocalFieldStyle((prev) => {
                            const next = { ...prev };
                            const merged = { ...(next[field.key] || {}), ...patch };
                            // Drop entries explicitly set to undefined → admin
                            // chose "default" — keeps the persisted map clean.
                            for (const k of Object.keys(merged)) {
                              if (merged[k] === undefined || merged[k] === null || merged[k] === '') delete merged[k];
                            }
                            if (Object.keys(merged).length) next[field.key] = merged;
                            else delete next[field.key];
                            return next;
                          });
                        };
                        return (
                        <div
                          key={field.key}
                          ref={(n) => { if (n) fieldRefs.current[field.key] = n; }}
                          className={[
                            'rounded-lg transition-all',
                            highlightKey === field.key ? 'ring-2 ring-blue-500 ring-offset-2 bg-blue-50/30 p-2 -m-2' : '',
                          ].join(' ')}
                        >
                          {field.type !== 'toggle' && (
                            <div className="flex items-center justify-between mb-1 gap-2">
                              <label className="block text-[11px] font-medium text-gray-700">
                                {field.label}
                              </label>
                              {STYLEABLE && (
                                <button
                                  type="button"
                                  onClick={() => setStyleOpenForField((cur) => cur === field.key ? null : field.key)}
                                  title="ستايل هذا الحقل (حجم/سُمك/لون)"
                                  className={`p-0.5 rounded transition-colors ${fStyleOpen ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-blue-600'}${Object.keys(fStyle).length ? ' ring-1 ring-blue-300' : ''}`}
                                >
                                  <Palette className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                          {fStyleOpen && STYLEABLE && (
                            <div className="mb-2 p-2 bg-blue-50/40 border border-blue-100 rounded-md space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">
                                  <span>الحجم ({fStyle.fontSize ?? 'افتراضي'})</span>
                                  <input type="range" min={8} max={120} step={1}
                                    value={fStyle.fontSize ?? 16}
                                    onChange={(e) => setFStyle({ fontSize: Number(e.target.value) })}
                                    className="h-1 accent-blue-600" />
                                </label>
                                <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">
                                  <span>السُمك ({fStyle.fontWeight ?? 'افتراضي'})</span>
                                  <input type="range" min={100} max={900} step={100}
                                    value={fStyle.fontWeight ?? 500}
                                    onChange={(e) => setFStyle({ fontWeight: Number(e.target.value) })}
                                    className="h-1 accent-blue-600" />
                                </label>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-600">اللون</span>
                                <input type="color" value={fStyle.color || '#000000'}
                                  onChange={(e) => setFStyle({ color: e.target.value })}
                                  className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0" />
                                <input type="text" value={fStyle.color || ''}
                                  onChange={(e) => setFStyle({ color: e.target.value })}
                                  placeholder="#000000"
                                  className="flex-1 px-2 py-1 border border-gray-200 rounded-md text-[11px] font-mono"
                                  dir="ltr" />
                                {Object.keys(fStyle).length > 0 && (
                                  <button type="button"
                                    onClick={() => setLocalFieldStyle((prev) => { const n = { ...prev }; delete n[field.key]; return n; })}
                                    className="text-[10px] text-red-500 hover:text-red-600 whitespace-nowrap">
                                    إعادة تعيين
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          <FieldInput
                            field={field}
                            value={localOverrides[field.key] !== undefined ? localOverrides[field.key] : field.default}
                            onChange={(v) => setLocalOverrides((prev) => {
                              const next = { ...prev };
                              if (JSON.stringify(v) === JSON.stringify(field.default)) {
                                delete next[field.key];
                              } else {
                                next[field.key] = v;
                              }
                              return next;
                            })}
                          />
                        </div>
                        );
                      })}
                    </div>
                  </div>
                    );
                  });
                })()}

                {/* ── Custom blocks (Phase 2/3) ────────────────────── */}
                {localCustomBlocks.length > 0 && (
                  <div className="mt-2 pt-3 border-t border-dashed border-gray-200">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Layers className="w-3.5 h-3.5 text-blue-500" />
                      <h3 className="text-[11px] font-bold text-gray-700">أقسام مخصّصة (مضافة)</h3>
                    </div>
                    <div className="space-y-2">
                      {localCustomBlocks
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((b, i, arr) => {
                          const def = blockCatalog?.blocks?.[b.type];
                          const blockHidden = b.enabled === false;
                          const isDragging = dragBlockId === b.id;
                          const isDragOver = dragOverBlockId === b.id;
                          // DnD handlers — same pattern as core sections.
                          const blockDragHandlers = {
                            draggable: true,
                            onDragStart: (e) => {
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', b.id);
                              setDragBlockId(b.id);
                            },
                            onDragOver: (e) => {
                              if (!dragBlockId || dragBlockId === b.id) return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverBlockId(b.id);
                            },
                            onDragLeave: () => {
                              if (dragOverBlockId === b.id) setDragOverBlockId(null);
                            },
                            onDrop: (e) => {
                              e.preventDefault();
                              const sourceId = e.dataTransfer.getData('text/plain') || dragBlockId;
                              if (!sourceId || sourceId === b.id) {
                                setDragBlockId(null);
                                setDragOverBlockId(null);
                                return;
                              }
                              setLocalCustomBlocks((prev) => {
                                const sortedPrev = [...prev].sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
                                const sourceIdx = sortedPrev.findIndex((x) => x.id === sourceId);
                                const destIdx = sortedPrev.findIndex((x) => x.id === b.id);
                                if (sourceIdx < 0 || destIdx < 0) return prev;
                                const [moved] = sortedPrev.splice(sourceIdx, 1);
                                sortedPrev.splice(destIdx, 0, moved);
                                return sortedPrev.map((x, j) => ({ ...x, order: j }));
                              });
                              setDragBlockId(null);
                              setDragOverBlockId(null);
                            },
                            onDragEnd: () => {
                              setDragBlockId(null);
                              setDragOverBlockId(null);
                            },
                          };
                          return (
                            <div
                              key={b.id}
                              {...blockDragHandlers}
                              className={[
                                'rounded-xl border overflow-hidden transition-all',
                                blockHidden ? 'border-amber-300 bg-amber-50/30' : 'border-blue-100 bg-blue-50/20',
                                isDragging ? 'opacity-50' : '',
                                isDragOver ? 'border-blue-500 ring-2 ring-blue-300 shadow-md' : '',
                              ].join(' ')}>
                              <div className="px-3 py-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <span title="اسحب لإعادة الترتيب" className="cursor-grab active:cursor-grabbing text-blue-300 hover:text-blue-500 shrink-0">
                                    <GripVertical className="w-3.5 h-3.5" />
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <h4 className={`text-[11px] font-bold truncate ${blockHidden ? 'text-amber-700 line-through' : 'text-blue-800'}`}>
                                      {def?.label?.ar || b.type}
                                      {blockHidden && <span className="mr-2 font-normal no-underline">(مخفي)</span>}
                                    </h4>
                                    <p className="text-[10px] text-blue-400 font-mono truncate" dir="ltr">{b.type}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  <button type="button" onClick={() => moveCustomBlock(b.id, -1)} disabled={i === 0}
                                    title="حرّك للأعلى"
                                    className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed">
                                    <ChevronUp className="w-3.5 h-3.5" />
                                  </button>
                                  <button type="button" onClick={() => moveCustomBlock(b.id, +1)} disabled={i === arr.length - 1}
                                    title="حرّك للأسفل"
                                    className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed">
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  </button>
                                  <button type="button" onClick={() => toggleCustomBlock(b.id)}
                                    title={blockHidden ? 'أظهر' : 'أخفِ'}
                                    className={`p-1 transition-colors ${blockHidden ? 'text-amber-600' : 'text-gray-500 hover:text-blue-600'}`}>
                                    {blockHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                  </button>
                                  <button type="button" onClick={() => duplicateCustomBlock(b.id)}
                                    title="نسخ"
                                    className="p-1 text-gray-500 hover:text-blue-600">
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                  <button type="button" onClick={() => setEditingBlockId(b.id)}
                                    title="تعديل المحتوى"
                                    className="p-1 text-blue-600 hover:text-blue-800 font-bold">
                                    <Edit3 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* + Add Section button — opens picker modal */}
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-blue-300 text-blue-700 bg-blue-50/40 hover:bg-blue-50 hover:border-blue-400 transition-colors text-sm font-bold"
                >
                  <PlusSquare className="w-4 h-4" />
                  إضافة قسم جديد
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Block Picker Modal — Phase 3 */}
      <BlockPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(blockType) => addCustomBlock(blockType)}
      />

      {/* Block Edit Panel — slides in from the right (LTR) when a custom
          block is selected for editing. */}
      {editingBlock && editingBlockDef && (
        <BlockEditPanel
          block={editingBlock}
          blockDef={editingBlockDef}
          onChange={(patch) => updateCustomBlock(editingBlock.id, patch)}
          onDelete={() => deleteCustomBlock(editingBlock.id)}
          onDuplicate={() => duplicateCustomBlock(editingBlock.id)}
          onClose={() => setEditingBlockId(null)}
        />
      )}

      {/* Phase 5 — Version history modal */}
      <HistoryPanel
        open={historyOpen}
        pageKey={pageKey}
        onClose={() => setHistoryOpen(false)}
        onRestored={() => { refetch?.(); setIframeNonce((n) => n + 1); }}
      />

      {/* Fallback panel for blocks with unknown types — schema not in catalog.
          Lets the admin delete them so their page doesn't end up with
          un-editable junk. */}
      {editingBlock && !editingBlockDef && (
        <div className="fixed inset-y-0 left-0 z-[90] w-full sm:w-[420px] bg-white border-r border-slate-200 shadow-2xl flex flex-col" dir="rtl">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-amber-700 truncate">قسم غير معروف</h3>
              <p className="text-[11px] text-slate-400 font-mono truncate" dir="ltr">{editingBlock.type}</p>
            </div>
            <button type="button" onClick={() => setEditingBlockId(null)}
              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 font-bold mb-2">⚠ نوع غير معروف</p>
              <p className="text-[12px] text-amber-700 leading-relaxed mb-4">
                هذا القسم محفوظ في الموقع بنوع لم يعد مدعوماً. لا يمكن تعديل محتواه. الخيار المتاح هو حذفه.
              </p>
              <button type="button"
                onClick={() => { deleteCustomBlock(editingBlock.id); setEditingBlockId(null); }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700">
                حذف هذا القسم نهائياً
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteBuilder;
